import React, { useContext } from 'react'
import styled from 'styled-components'
import { Option, ReqColumnWithMetaData } from './types'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { Checkbox, Input } from '@seiue/ui'
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd'
import { FieldTypeEnum } from 'packages/sdks-next/growp'
import { QuestionnaireContext } from './context'
import { cloneDeep } from '@seiue/util'
import { getTheme } from '@/utils/theme'
const theme = getTheme()

/**
 * 复合单选|多选 - 选项设置
 *
 * @param param - 参数
 * @param param.options - 选项
 * @param param.type - 类型|单选或多选
 * @param param.currentChild - 当前项
 * @param param.childIndex - 当前项索引
 * @param param.onChange - 状态变化时的回调
 * @param param.otherRequire - 其它是否必填
 * @param param.isUserField - 是否是用户字段
 * @returns React.ReactNode
 */
export const ProductOptionSetting: React.FC<{
  options: Option[]
  type: FieldTypeEnum.Single | FieldTypeEnum.Multi
  currentChild: ReqColumnWithMetaData
  childIndex: number
  onChange: (checked: boolean) => void
  otherRequire?: boolean
  isUserField?: boolean
}> = ({
  options,
  type,
  otherRequire,
  isUserField,
  currentChild,
  childIndex,
  onChange,
}) => {
  const { questionnaires, setQuestionnaires, activeItemIndex, structIsChange } =
    useContext(QuestionnaireContext)

  const onDelete = (index: number) => {
    const preOptions = cloneDeep(options)
    preOptions.splice(index, 1)
    const newChild = { ...currentChild, options: preOptions }
    const preQuestionnaire = cloneDeep(questionnaires)
    preQuestionnaire[activeItemIndex || 0].children?.splice(
      childIndex,
      1,
      newChild
    )
    setQuestionnaires([...preQuestionnaire])
    structIsChange && structIsChange()
  }
  const onChangeOptionLabel = (param: Option, index: number) => {
    const preOptions = cloneDeep(options)
    preOptions.splice(index, 1, param)
    const newChild = { ...currentChild, options: preOptions }
    const preQuestionnaire = cloneDeep(questionnaires)
    preQuestionnaire[activeItemIndex || 0].children?.splice(
      childIndex,
      1,
      newChild
    )
    setQuestionnaires([...preQuestionnaire])
  }
  const onDragEnd: OnDragEndResponder = (result) => {
    const { source, destination } = result
    if (
      source.droppableId !== 'setting' ||
      !destination ||
      destination?.droppableId !== 'setting' ||
      source.index === destination.index
    ) {
      return
    }
    const sourceOption = options[source.index]
    options.splice(source.index, 1)
    options.splice(destination.index, 0, sourceOption)
    structIsChange && structIsChange()
  }
  return (
    <Wrapper>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId="setting"
          direction="vertical"
        >
          {(provided) => {
            return (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {options.map((option, index) => {
                  return (
                    <Draggable
                      draggableId={`${index}`}
                      index={index}
                      key={index}
                      isDragDisabled={!!isUserField}
                    >
                      {(provided) => (
                        <OptionWrapper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {!isUserField ? (
                            <GrowpIcon name="DragVertical" />
                          ) : null}
                          {option.value === '其它' ? (
                            <OtherWrapper>
                              <div>其它</div>
                              <Input disabled />
                              <Checkbox
                                checked={otherRequire}
                                onChange={(e) => onChange(e.target.checked)}
                              >
                                必填项
                              </Checkbox>
                            </OtherWrapper>
                          ) : (
                            <Input
                              disabled={!!isUserField}
                              placeholder="请输入"
                              value={option.label}
                              style={{
                                width: isUserField ? '264px' : '212px',
                              }}
                              onChange={(e) => {
                                onChangeOptionLabel(
                                  {
                                    ...option,
                                    label: e.target.value,
                                    name: e.target.value,
                                  },
                                  index
                                )
                              }}
                            />
                          )}

                          {(type === FieldTypeEnum.Single &&
                            options.length > 1 &&
                            !isUserField) ||
                          (type === FieldTypeEnum.Multi &&
                            options.length > 2 &&
                            !isUserField) ? (
                            <GrowpIcon
                              name="Delete"
                              color={theme.text._4}
                              onClick={() => onDelete(index)}
                            />
                          ) : (
                            <div
                              style={{ width: isUserField ? '0' : '16px' }}
                            />
                          )}
                        </OptionWrapper>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )
          }}
        </Droppable>
      </DragDropContext>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const OptionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: ${(props) => props.theme.text._2};
  cursor: pointer;
  &:last-child {
    margin-bottom: 0;
  }
  &&& {
    .ant-input {
      min-width: 212px;
      height: 32px;
      border-radius: 8px;
      border: 1px solid transparent;
      background-color: ${(props) => props.theme.background._3};
    }
    .ant-input:focus {
      border-color: ${(props) => props.theme.brand._1} !important;
    }
  }
`

const OtherWrapper = styled.div`
  width: 212px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &&& {
    .ant-input {
      min-width: 112px;
      width: 112px;
    }
    .ant-checkbox + span {
      padding-right: 0;
    }
  }
`
